import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageCircle, Car, Calendar, Headphones, 
  User, Clock, ChevronRight, Inbox, AlertTriangle, Shield,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getProfilePhotoUrl } from '../lib/imageUtils';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

const SupportInboxPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [threads, setThreads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchInbox();
  }, [activeFilter]);

  const fetchInbox = async () => {
    setLoading(true);
    try {
      let url = '/platform-owner/support-inbox';
      if (activeFilter !== 'all') {
        url += `?filter_type=${activeFilter}`;
      }
      const response = await api.get(url);
      setThreads(response.data.threads || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load support inbox');
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const getParticipantNames = (thread) => {
    const names = thread.participants?.map(p => p.full_name || p.email).filter(Boolean) || [];
    if (names.length <= 2) return names.join(' & ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(parseISO(timestamp), 'd MMM yyyy, h:mm a');
    } catch {
      return '';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'booking': return <Calendar className="w-4 h-4" />;
      case 'support': return <Headphones className="w-4 h-4" />;
      case 'dispute': return <AlertTriangle className="w-4 h-4" />;
      case 'inquiry': return <Car className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'booking': return 'Booking';
      case 'support': return 'Support';
      case 'dispute': return 'Dispute';
      case 'inquiry': return 'Inquiry';
      default: return category;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'booking': return 'bg-blue-500/20 text-blue-400';
      case 'support': return 'bg-purple-500/20 text-purple-400';
      case 'dispute': return 'bg-red-500/20 text-red-400';
      case 'inquiry': return 'bg-green-500/20 text-green-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  // Header badge for the shell
  const headerBadge = stats && (
    <Badge className="bg-purple-500/20 text-purple-300 border-0">
      {stats.unread} unread
    </Badge>
  );

  return (
    <PlatformOwnerAppShell 
      title="Support Inbox" 
      activeId="support-inbox"
      headerBadge={headerBadge}
    >
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-white/60">All Threads</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-purple-400">{stats.support}</p>
              <p className="text-xs text-white/60">Support</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-blue-400">{stats.bookings}</p>
              <p className="text-xs text-white/60">Bookings</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-red-400">{stats.disputes}</p>
              <p className="text-xs text-white/60">Disputes</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Tabs */}
      <Tabs value={activeFilter} onValueChange={setActiveFilter} className="mb-4">
        <TabsList className="grid w-full grid-cols-5 h-auto bg-white/5 border border-white/10">
          <TabsTrigger value="all" className="text-xs sm:text-sm py-2 data-[state=active]:bg-purple-600">All</TabsTrigger>
          <TabsTrigger value="bookings" className="text-xs sm:text-sm py-2 data-[state=active]:bg-purple-600">Bookings</TabsTrigger>
          <TabsTrigger value="support" className="text-xs sm:text-sm py-2 data-[state=active]:bg-purple-600">Support</TabsTrigger>
          <TabsTrigger value="disputes" className="text-xs sm:text-sm py-2 data-[state=active]:bg-purple-600">Disputes</TabsTrigger>
          <TabsTrigger value="admin" className="text-xs sm:text-sm py-2 data-[state=active]:bg-purple-600">Admin</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Info Banner */}
      <Card className="bg-yellow-500/10 border-yellow-500/30 mb-4">
        <CardContent className="p-3 flex items-center gap-3">
            <Shield className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <p className="text-sm text-yellow-200">
              <span className="font-medium">Moderator View:</span> You can view and reply to any conversation. Your replies will appear as "HireHub Support".
            </p>
          </CardContent>
        </Card>

        {/* Thread List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : threads.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Inbox className="w-12 h-12 text-white/30 mb-4" />
              <h3 className="font-medium text-white mb-1">No conversations found</h3>
              <p className="text-sm text-white/60">
                {activeFilter === 'all' 
                  ? 'No conversations on the platform yet'
                  : `No ${getCategoryLabel(activeFilter).toLowerCase()} conversations`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {threads.map((thread) => {
              const firstParticipant = thread.participants?.[0];
              const photoUrl = getProfilePhotoUrl(firstParticipant?.profile_photo);
              
              return (
                <Link
                  key={thread.thread_id}
                  to={`/messages/${thread.thread_id}`}
                  className="block"
                  data-testid={`inbox-thread-${thread.thread_id}`}
                >
                  <Card className={`bg-white/5 border-white/10 hover:border-purple-500/50 transition-colors ${thread.unread_count > 0 ? 'bg-purple-500/10' : ''}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {photoUrl ? (
                            <img 
                              src={photoUrl}
                              alt={firstParticipant?.full_name}
                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList?.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-semibold ${photoUrl ? 'hidden' : ''}`}>
                            {firstParticipant?.full_name?.charAt(0)?.toUpperCase() || <User className="w-5 h-5" />}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className={`font-medium text-sm sm:text-base truncate ${thread.unread_count > 0 ? 'text-white' : 'text-white/80'}`}>
                                  {getParticipantNames(thread)}
                                </h3>
                                <Badge className={`text-[10px] sm:text-xs flex items-center gap-1 ${getCategoryColor(thread.category)}`}>
                                  {getCategoryIcon(thread.category)}
                                  <span className="hidden sm:inline">{getCategoryLabel(thread.category)}</span>
                                </Badge>
                              </div>
                              
                              {/* Vehicle/Booking reference */}
                              {thread.vehicle && (
                                <p className="text-xs text-white/50 mt-0.5">
                                  {thread.vehicle.make} {thread.vehicle.model}
                                </p>
                              )}
                              {thread.booking && (
                                <p className="text-xs text-blue-400 mt-0.5">
                                  Booking: {thread.booking.status}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {thread.unread_count > 0 && (
                                <Badge className="bg-purple-600 text-white text-xs">
                                  {thread.unread_count}
                                </Badge>
                              )}
                              <ChevronRight className="w-4 h-4 text-white/40" />
                            </div>
                          </div>

                          {/* Last message preview */}
                          {thread.last_message && (
                            <p className={`text-sm mt-1 truncate ${thread.unread_count > 0 ? 'text-white/80' : 'text-white/50'}`}>
                              {thread.last_message.sender?.is_support && (
                                <span className="text-yellow-400 font-medium">Support: </span>
                              )}
                              {thread.last_message.body}
                            </p>
                          )}

                          {/* Timestamp */}
                          {thread.last_message_at && (
                            <p className="text-xs text-white/40 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(thread.last_message_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
    </PlatformOwnerAppShell>
  );
};

export default SupportInboxPage;
